/**
 * Full-app OP ID gate (DEC-166): no identity, no app. Mirrors OPSetlist's
 * login.html decision shape — poll for a settled answer for a few seconds
 * (opid-client's init is async: broker call, or offline-unlock fallback) so
 * a user who's about to be signed in a few hundred ms later never sees a
 * flash of the redirect screen, then either render children or send them to
 * the hub with `?next=` back to here.
 *
 * OPButtons is not a sign-in surface itself — there is no local form, the hub
 * is the only door (same rule as OPSetlist since 2026-07-27).
 */

import { createSignal, onMount, Show, type JSX } from 'solid-js';
import { authStatus, authSignedOutReason, initAuth } from '../auth/opid.ts';
import { isOnSuiteDomain } from '../util/env.ts';

// The suite sign-in page moved here from /opadmin on 2026-09-28 (OPApps);
// /opadmin still forwards with query and hash, so older builds keep working.
const HUB = 'https://overpoweredapps.net/signin';
const DECIDE_DEADLINE_MS = 3000;

function hubUrlWithReturn(): string {
  return `${HUB}?next=${encodeURIComponent(location.href)}`;
}

export function SignInGate(props: { children: JSX.Element }): JSX.Element {
  // OP ID's master cookie is scoped to `.overpoweredapps.net` — off that
  // domain there is no session to ever adopt, broker or offline-cache. That's
  // the dev server (`localhost:5183`) AND the gig LAN-IP path (an iPad
  // hitting the host machine's IP directly, still the primary on-stage
  // connection model — DEC-166 only moved the PWA's HOSTING, OPBridge's LAN
  // pairing is unchanged). Gating either would be a permanent redirect loop
  // to a broker that can never authenticate them, so the gate applies ONLY on
  // the actual suite subdomain; every other origin renders ungated.
  if (!isOnSuiteDomain()) return <>{props.children}</>;

  const [settled, setSettled] = createSignal(false);
  const [offlineNoCache, setOfflineNoCache] = createSignal(false);

  onMount(() => {
    const startedAt = Date.now();
    void initAuth();

    (function decide() {
      const s = authStatus();
      if (s === 'signed-in' || s === 'offline') {
        setSettled(true);
        return;
      }
      if (Date.now() - startedAt < DECIDE_DEADLINE_MS) {
        setTimeout(decide, 100);
        return;
      }
      // Settled: signed out. Two very different reasons, and only one of them
      // is a reason to navigate away.
      //
      // `navigator.onLine` is NOT the test (2026-09-01): it reports whether a
      // network interface exists, not whether the internet is reachable, so on
      // a machine sitting on the gig LAN with no uplink it is `true`. That sent
      // the browser to a hub it could not reach — a dead ERR_FAILED page with
      // no way back and no explanation, in precisely the offline scenario the
      // screen below was written for. Reproduced on the Mac: no route off the
      // LAN, redirect fired anyway.
      //
      // The client already knows which case this is: `offline-no-cache` means
      // the broker call THREW (unreachable), `no-session` means the broker
      // answered 401. Only the latter can be resolved by visiting the hub.
      const reason = authSignedOutReason();
      if (reason === 'offline-no-cache' || !navigator.onLine) {
        setOfflineNoCache(true);
        setSettled(true);
        return;
      }
      location.replace(hubUrlWithReturn());
    })();
  });

  return (
    <Show when={settled() && (authStatus() === 'signed-in' || authStatus() === 'offline')} fallback={<GateScreen offlineNoCache={offlineNoCache()} />}>
      {props.children}
    </Show>
  );
}

function GateScreen(props: { offlineNoCache: boolean }): JSX.Element {
  return (
    <div
      style={{
        position: 'fixed', inset: '0', display: 'grid', 'place-items': 'center',
        background: '#03060c', color: '#c7f2ff',
        'font-family': "'Inter','Segoe UI',system-ui,-apple-system,sans-serif",
        padding: '1.5rem', 'text-align': 'center',
      }}
    >
      <div>
        <div style={{ 'font-weight': '700', 'font-size': '1.4rem', 'letter-spacing': '.02em', 'margin-bottom': '0.75rem' }}>
          {/* SPLIT ACROSS TWO ELEMENTS, so no search for the product name in
              any casing can find it - "OPKonnex" is never a contiguous string
              here. This survived the whole rename on the sign-in screen, which
              is the FIRST surface a re-installing device sees, while the prose
              two elements below it was rewritten correctly. If this wordmark is
              ever restyled, keep the halves greppable or it will rot again. */}
          OP<span style={{ color: '#22d3ee' }}>Buttons</span>
        </div>
        <Show
          when={!props.offlineNoCache}
          fallback={
            <>
              <p style={{ color: '#c7f2ff', margin: '0 0 0.4rem' }}>Can't reach OverpoweredApps.</p>
              <p style={{ color: '#6f9cb8', 'font-size': '0.85rem', margin: '0 0 1rem' }}>
                This device has no internet and has never signed in here, so there's no
                saved sign-in to unlock. Reconnect once and OPButtons keeps working offline
                after that.
              </p>
              {/* Without this the screen is a dead end — the operator has to know to
                  reload by hand at the exact moment the uplink returns. */}
              <button
                type="button"
                onClick={() => location.reload()}
                style={{
                  background: 'transparent', color: '#22d3ee', cursor: 'pointer',
                  border: '1px solid #22d3ee', 'border-radius': '6px',
                  padding: '0.5rem 1.25rem', font: 'inherit', 'font-size': '0.85rem',
                }}
              >
                Try again
              </button>
            </>
          }
        >
          <p style={{ color: '#6f9cb8', 'font-size': '0.9rem', margin: '0' }}>Taking you to OverpoweredApps to sign in...</p>
        </Show>
      </div>
    </div>
  );
}
